import { z } from 'zod';
import { PartnerType, PartnerStatus } from '../enums/partnerType';
import { BooksIntegrationProvider } from '../enums/booksIntegrationProvider';
import {
  billResponseSchema,
  generateBillsByOrgResponseSchema,
  slabPricingConfigSchema,
} from './admin';

// ============================================
// Partner Schemas
// ============================================

export const partnerSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.nativeEnum(PartnerType),
  hostname: z.string().nullable().optional(),
  status: z.nativeEnum(PartnerStatus),
  metadata: z.record(z.unknown()).nullable().optional(),
  supportEmail: z.string().nullable().optional(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});
export type Partner = z.infer<typeof partnerSchema>;

export const partnersResponseSchema = z.array(partnerSchema);

// Create partner request
export const createPartnerRequestSchema = z.object({
  name: z.string().min(1, 'Partner name is required'),
  type: z.nativeEnum(PartnerType),
  hostname: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
});
export type CreatePartnerRequest = z.infer<typeof createPartnerRequestSchema>;

// Update partner request
export const updatePartnerRequestSchema = z.object({
  name: z.string().min(1).optional(),
  hostname: z.string().nullable().optional(),
  status: z.nativeEnum(PartnerStatus).optional(),
  metadata: z.record(z.unknown()).nullable().optional(),
});
export type UpdatePartnerRequest = z.infer<typeof updatePartnerRequestSchema>;

// ============================================
// Partner Admin Schemas
// ============================================

export const partnerAdminSchema = z.object({
  employeeId: z.number(),
  partnerId: z.number().optional(),
  employeeName: z.string().optional(),
  employeeEmail: z.string().optional(),
});
export type PartnerAdmin = z.infer<typeof partnerAdminSchema>;

export const partnerAdminsResponseSchema = z.array(partnerAdminSchema);

// Response for adding a partner admin
export const addPartnerAdminResponseSchema = z.object({
  employeeId: z.number(),
  partnerId: z.number(),
  employeeName: z.string().optional(),
  employeeEmail: z.string().optional(),
});

// Add partner admin request (by email)
export const addPartnerAdminRequestSchema = z.object({
  email: z
    .string()
    .email('Valid email is required')
    .transform(val => val.toLowerCase()),
});
export type AddPartnerAdminRequest = z.infer<
  typeof addPartnerAdminRequestSchema
>;

// ============================================
// Partner Dashboard Schemas (what partner admins see)
// ============================================

// Partner context returned from GET /partner/me
export const partnerContextSchema = z.object({
  partnerId: z.number(),
  partnerName: z.string(),
  partnerType: z.nativeEnum(PartnerType),
  /* Books-platform integration the partner opted into (null = none). Drives
     partner-only UI like the Odoo mapping columns; resolved server-side from
     partner.metadata — see backend resolveBooksIntegration. */
  booksIntegration: z.nativeEnum(BooksIntegrationProvider).nullable(),
});
export type PartnerContext = z.infer<typeof partnerContextSchema>;

// Org as seen by partner admin
export const partnerOrgSchema = z.object({
  id: z.number(),
  name: z.string(),
  hostname: z.string(),
  businessCount: z.number(),
  createdAt: z.union([z.string(), z.date()]),
  /* The partner's own customer pricing for this org (they set it from the
     partner dashboard). Never carries defaultBillType/defaultZohoId. */
  slabPricing: slabPricingConfigSchema.nullish(),
});
export type PartnerOrg = z.infer<typeof partnerOrgSchema>;

export const partnerOrgsResponseSchema = z.array(partnerOrgSchema);

// Business as seen by partner admin (scoped to an org)
export const partnerBusinessSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable().optional(),
  bizWhatsappNumber: z.string().nullable().optional(),
  countryCode: z.number().nullable().optional(),
  signUpType: z.string().nullable().optional(),
  createdAt: z.union([z.string(), z.date()]),
  /* Present ONLY when the partner has a books integration (the backend omits
     them otherwise, so other partners never receive Heltar-internal ids). */
  booksCustomerId: z.string().nullable().optional(),
  booksUsername: z.string().nullable().optional(),
});
export type PartnerBusiness = z.infer<typeof partnerBusinessSchema>;

export const partnerBusinessesResponseSchema = z.array(partnerBusinessSchema);

// Business row for the partner-wide flat Businesses tab (adds the owning org
// and the WhatsApp Business Account / phone number ids)
export const partnerBusinessWithOrgSchema = partnerBusinessSchema.extend({
  orgId: z.number(),
  orgName: z.string().nullable().optional(),
  businessAccountId: z.string().nullable().optional(),
  phoneNumberId: z.string().nullable().optional(),
});
export type PartnerBusinessWithOrg = z.infer<
  typeof partnerBusinessWithOrgSchema
>;

export const partnerAllBusinessesResponseSchema = z.array(
  partnerBusinessWithOrgSchema,
);

// ============================================
// Partner Billing Schemas
// ============================================

/* Bill row as exposed to a partner admin. Deliberately FLAT and .strict():
   no volumeSource/remarks (bill type stays invisible to partners) and no
   nested business/org entities (which carry Heltar-internal fields).
   validateResponse only validates — it does not strip — so strictness is the
   fail-closed leak guard. */
export const partnerBillSchema = z
  .object({
    id: z.string().uuid(),
    businessId: z.number(),
    businessName: z.string(),
    businessAccountId: z.string().nullable(),
    phoneNumber: z.string(),
    orgId: z.number().nullable(),
    orgName: z.string().nullable(),
    periodStart: z.string(),
    periodEnd: z.string(),
    billData: billResponseSchema,
    createdAt: z.union([z.string(), z.date()]),
    updatedAt: z.union([z.string(), z.date()]),
  })
  .strict();
export type PartnerBill = z.infer<typeof partnerBillSchema>;

export const partnerBillsResponseSchema = z.object({
  bills: z.array(partnerBillSchema),
  total: z.number(),
  page: z.number().optional(),
  limit: z.number().optional(),
});
export type PartnerBillsResponse = z.infer<typeof partnerBillsResponseSchema>;

/* Org pricing update from the partner dashboard. STRICT single-field
   whitelist — partners must never be able to set defaultBillType,
   defaultZohoId, or anything else on the org. null clears the pricing. */
export const partnerOrgPricingUpdateRequestSchema = z
  .object({
    slabPricing: slabPricingConfigSchema.nullable(),
  })
  .strict();
export type PartnerOrgPricingUpdateRequest = z.infer<
  typeof partnerOrgPricingUpdateRequestSchema
>;

export const partnerOrgPricingResponseSchema = z.object({
  id: z.number(),
  name: z.string(),
  slabPricing: slabPricingConfigSchema.nullable(),
  updatedAt: z.union([z.string(), z.date()]),
});
export type PartnerOrgPricingResponse = z.infer<
  typeof partnerOrgPricingResponseSchema
>;

/* Generate-all response: one run per partner org (same whitelisted per-org
   shape the single-org endpoint exposes) plus the aggregate counts. Strict at
   the top level so a leaked internal field 500s at validateResponse instead
   of shipping to the partner dashboard. */
export const generatePartnerAllBillsResponseSchema = z
  .object({
    totalOrgs: z.number(),
    totalBusinesses: z.number(),
    successCount: z.number(),
    failedCount: z.number(),
    orgs: z.array(generateBillsByOrgResponseSchema),
  })
  .strict();
export type GeneratePartnerAllBillsResponse = z.infer<
  typeof generatePartnerAllBillsResponseSchema
>;

// Assign partner to org request (from admin dashboard)
export const assignPartnerToOrgRequestSchema = z.object({
  partnerId: z.number().nullable(),
});
export type AssignPartnerToOrgRequest = z.infer<
  typeof assignPartnerToOrgRequestSchema
>;

// ============================================
// Partner Support Email Config Schemas
// ============================================

// Update support email config request (from partner dashboard)
// supportEmail: Partner's support email used for sending emails on their behalf (e.g. password reset)
export const updateEmailConfigRequestSchema = z.object({
  supportEmail: z.string().email('Valid email is required'),
  supportEmailPassword: z.string().min(1, 'App password is required'),
});
export type UpdateEmailConfigRequest = z.infer<
  typeof updateEmailConfigRequestSchema
>;

// Support email config response (masked password)
export const emailConfigResponseSchema = z.object({
  supportEmail: z.string().nullable(),
  isConfigured: z.boolean(),
});
export type EmailConfigResponse = z.infer<typeof emailConfigResponseSchema>;
